import React from 'react';
import { cn } from '../../../lib/utils';

export interface AgeInputProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  helperText?: string;
  min?: number;
  max?: number;
  className?: string;
  disabled?: boolean;
}

export const AgeInput: React.FC<AgeInputProps> = ({
  label,
  value,
  onChange,
  placeholder = 'e.g., 12',
  helperText,
  min = 0,
  max = 120,
  className = '',
  disabled = false
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') {
      onChange(null);
    } else {
      const numValue = parseInt(inputValue);
      if (!isNaN(numValue)) {
        onChange(numValue);
      }
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? ''}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none transition-colors',
          '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{
          backgroundColor: disabled ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
          borderColor: disabled ? 'var(--color-border-muted)' : 'var(--color-border)',
          color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
          minHeight: '44px',
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'default'
        } as React.CSSProperties}
        onFocus={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--color-accent)';
            e.currentTarget.style.boxShadow = '0 0 0 1px var(--color-accent)';
          }
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = disabled ? 'var(--color-border-muted)' : 'var(--color-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--color-border-hover)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && document.activeElement !== e.currentTarget) {
            e.currentTarget.style.borderColor = 'var(--color-border)';
          }
        }}
      />
      {helperText && (
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
          {helperText}
        </p>
      )}
    </div>
  );
};

